import React, { useState } from 'react';
import { View, Text, TouchableOpacity, Modal, ScrollView, StyleSheet } from 'react-native';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import { SVG_COMPONENTS } from '@/assets/admin';

interface MultiSelectOption {
  label: string;
  value: string;
}

interface MultiSelectPickerProps {
  label: string;
  options: MultiSelectOption[];
  selectedValues: string[];
  onSelectionChange: (values: string[]) => void;
  placeholder?: string;
  error?: string;
  required?: boolean;
}

export const MultiSelectPicker: React.FC<MultiSelectPickerProps> = ({
  label,
  options,
  selectedValues,
  onSelectionChange,
  placeholder = 'Select options',
  error,
  required = false,
}) => {
  const styles = useStyles();
  const { redesign } = useAdminColors();
  const [isModalVisible, setIsModalVisible] = useState(false);

  const handleOptionToggle = (value: string) => {
    const newSelection = selectedValues.includes(value)
      ? selectedValues.filter((v) => v !== value)
      : [...selectedValues, value];

    onSelectionChange(newSelection);
  };

  const getDisplayText = () => {
    if (selectedValues.length === 0) return placeholder;
    if (selectedValues.length === 1) return selectedValues[0];
    return `${selectedValues.length} selected`;
  };

  return (
    <View style={styles.container}>
      <Text style={styles.label}>
        {label}
        {required && <Text style={styles.required}> *</Text>}
      </Text>

      <TouchableOpacity
        style={[styles.selector, error && styles.selectorError]}
        onPress={() => setIsModalVisible(true)}
        activeOpacity={0.7}
      >
        <Text style={[styles.selectorText, selectedValues.length === 0 && styles.placeholderText]}>
          {getDisplayText()}
        </Text>
        <SVG_COMPONENTS.CHEVRON_DOWN width={20} height={20} color={redesign.text.dim} />
      </TouchableOpacity>

      {error && <Text style={styles.errorText}>{error}</Text>}

      <Modal
        visible={isModalVisible}
        animationType="slide"
        presentationStyle="pageSheet"
        onRequestClose={() => setIsModalVisible(false)}
      >
        <View style={styles.modal}>
          <View style={styles.modalHeader}>
            <TouchableOpacity style={styles.cancelButton} onPress={() => setIsModalVisible(false)}>
              <Text style={styles.cancelButtonText}>Cancel</Text>
            </TouchableOpacity>

            <Text style={styles.modalTitle}>{label}</Text>

            <TouchableOpacity style={styles.doneButton} onPress={() => setIsModalVisible(false)}>
              <Text style={styles.doneButtonText}>Done</Text>
            </TouchableOpacity>
          </View>

          <ScrollView style={styles.optionsList}>
            {options.map((option) => {
              const isSelected = selectedValues.includes(option.value);
              return (
                <TouchableOpacity
                  key={option.value}
                  style={styles.option}
                  onPress={() => handleOptionToggle(option.value)}
                  activeOpacity={0.7}
                >
                  <Text style={styles.optionText}>{option.label}</Text>
                  <View style={[styles.checkbox, isSelected && styles.checkboxSelected]}>
                    {isSelected && (
                      <SVG_COMPONENTS.CHECK_CIRCLE width={20} height={20} color="#FFFFFF" />
                    )}
                  </View>
                </TouchableOpacity>
              );
            })}
          </ScrollView>
        </View>
      </Modal>
    </View>
  );
};

const useStyles = () =>
  useThemedStyles(({ components, colors, semantic }) => {
    const admin = components.admin;
    return StyleSheet.create({
      container: {
        marginBottom: 16,
      },
      label: {
        fontSize: 14,
        fontWeight: '500',
        color: admin.formInput.label,
        marginBottom: 8,
      },
      required: {
        color: semantic.status.error,
      },
      selector: {
        backgroundColor: admin.selector.bg,
        borderRadius: 8,
        paddingHorizontal: 16,
        paddingVertical: 12,
        borderWidth: 1,
        borderColor: admin.selector.border,
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        minHeight: 48,
      },
      selectorError: {
        borderColor: semantic.status.error,
      },
      selectorText: {
        fontSize: 16,
        color: colors.text.primary,
        flex: 1,
      },
      placeholderText: {
        color: admin.formInput.placeholder,
      },
      errorText: {
        fontSize: 12,
        color: semantic.status.error,
        marginTop: 4,
      },
      modal: {
        flex: 1,
        backgroundColor: admin.selector.dropdownBg,
      },
      modalHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingHorizontal: 16,
        paddingVertical: 12,
        borderBottomWidth: 1,
        borderBottomColor: admin.dataTable.border,
        backgroundColor: admin.dataTable.headerBg,
      },
      cancelButton: {
        paddingVertical: 8,
        paddingHorizontal: 12,
      },
      cancelButtonText: {
        fontSize: 16,
        color: colors.text.dim,
      },
      modalTitle: {
        fontSize: 18,
        fontWeight: '600',
        color: colors.text.primary,
      },
      doneButton: {
        paddingVertical: 8,
        paddingHorizontal: 12,
      },
      doneButtonText: {
        fontSize: 16,
        fontWeight: '600',
        color: admin.formInput.borderFocused,
      },
      optionsList: {
        flex: 1,
      },
      option: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingHorizontal: 16,
        paddingVertical: 16,
        borderBottomWidth: 1,
        borderBottomColor: admin.dataTable.border,
      },
      optionText: {
        fontSize: 16,
        color: colors.text.primary,
        flex: 1,
      },
      checkbox: {
        width: 24,
        height: 24,
        borderRadius: 12,
        borderWidth: 2,
        borderColor: admin.selector.border,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'transparent',
      },
      checkboxSelected: {
        backgroundColor: admin.formInput.borderFocused,
        borderColor: admin.formInput.borderFocused,
      },
    });
  });
